import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../context/ThemeContext';
import { siteConfig } from '../data/site';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const { isDark } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { href: '#home', label: 'Início' },
    { href: '#servicos', label: 'Serviços' },
    { href: '#projetos', label: 'Portfólio' },
    { href: '#resultados', label: 'Resultados' },
    { href: '#depoimentos', label: 'Depoimentos' },
    { href: '#sobre', label: 'Sobre' },
    { href: '#faq', label: 'FAQ' },
    { href: '#contato', label: 'Contato' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);

    if (isHome) {
      scrollToSection(href);
      return;
    }

    navigate('/' + href);
  };

  const navBg =
    !isHome || isScrolled
      ? isDark
        ? 'bg-dark-bg/95 backdrop-blur-md shadow-lg border-b border-burnt/10'
        : 'bg-cream/95 backdrop-blur-md shadow-md border-b border-brown/10'
      : 'bg-transparent';

  const textColor =
    !isHome || isScrolled
      ? isDark
        ? 'text-cream'
        : 'text-dark'
      : 'text-white';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 font-heading ${navBg}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link
            to="/"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3"
            data-cursor="pointer"
          >
            {logoError ? (
              <span className="w-11 h-11 rounded-full bg-burnt flex items-center justify-center text-white font-bold text-sm">
                MS
              </span>
            ) : (
              <img
                src={siteConfig.logoRound}
                alt="MS Digitais"
                className="h-11 w-11 object-contain"
                onError={() => setLogoError(true)}
              />
            )}
            <span className={`font-heading font-bold text-lg hidden sm:block ${textColor}`}>
              MS Digitais
            </span>
          </Link>

          <div className="hidden lg:flex items-center space-x-6">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`text-sm font-medium transition-colors hover:text-burnt ${textColor}`}
                data-cursor="pointer"
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/orcamento"
              className="text-sm font-semibold transition-colors text-burnt hover:text-primary-700"
              data-cursor="pointer"
            >
              Orçamento
            </Link>
            <ThemeToggle />
          </div>

          <div className="flex lg:hidden items-center gap-3">
            <ThemeToggle />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-md transition-colors ${textColor}`}
              aria-label="Toggle menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className={`lg:hidden border-t ${isDark ? 'bg-dark-bg border-burnt/10' : 'bg-cream border-brown/10'}`}>
          <div className="px-4 pt-2 pb-4 space-y-1">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className={`block px-3 py-2 font-medium rounded-md transition-colors ${
                  isDark
                    ? 'text-cream hover:bg-burnt/10'
                    : 'text-dark hover:bg-brown/5'
                }`}
              >
                {item.label}
              </a>
            ))}
            <Link
              to="/orcamento"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-3 py-2 font-semibold rounded-md transition-colors ${
                isDark
                  ? 'text-burnt hover:bg-burnt/10'
                  : 'text-burnt hover:bg-brown/5'
              }`}
            >
              Orçamento
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
